import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Chip,
  Collapse,
  InputAdornment
} from '@material-ui/core';
import {
  FindReplace as ReplaceIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Close as CloseIcon,
  SelectAll as SelectAllIcon,
  Done as DoneIcon,
  Clear as ClearIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    position: 'absolute',
    top: 48, // Altura da toolbar
    right: 0,
    zIndex: 10,
    minWidth: 300,
    maxWidth: 400,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[4],
    padding: theme.spacing(2)
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  searchField: {
    flex: 1
  },
  matchInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    padding: theme.spacing(0.5, 0)
  },
  navigationButtons: {
    display: 'flex',
    gap: theme.spacing(0.5)
  },
  replaceSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

const SearchReplace = ({ 
  isOpen, 
  onClose, 
  value, 
  onChange, 
  textareaRef 
}) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [showReplace, setShowReplace] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [matches, setMatches] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(0);
  
  const searchInputRef = useRef(null);

  // Focar no input quando abrir
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Buscar matches quando o termo mudar
  useEffect(() => {
    if (searchTerm) {
      findMatches();
    } else {
      setMatches([]);
      setCurrentMatch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, caseSensitive, wholeWord, useRegex, value]);

  // Encontrar todas as ocorrências
  const findMatches = () => {
    if (!searchTerm) {
      setMatches([]);
      return;
    }

    try {
      let searchPattern;
      
      if (useRegex) {
        const flags = caseSensitive ? 'g' : 'gi';
        searchPattern = new RegExp(searchTerm, flags);
      } else {
        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        let pattern = escapedTerm;
        
        if (wholeWord) {
          pattern = `\\b${pattern}\\b`;
        }
        
        const flags = caseSensitive ? 'g' : 'gi';
        searchPattern = new RegExp(pattern, flags);
      }

      const foundMatches = [];
      let match;
      
      while ((match = searchPattern.exec(value)) !== null) {
        foundMatches.push({
          index: match.index,
          length: match[0].length,
          text: match[0]
        });
        
        // Prevenir loop infinito
        if (!searchPattern.global) break;
        if (match.index === searchPattern.lastIndex) {
          searchPattern.lastIndex++;
        }
      }

      setMatches(foundMatches);
      setCurrentMatch(foundMatches.length > 0 ? 0 : -1);
    } catch (error) {
      console.error('Erro na busca:', error);
      setMatches([]);
    }
  };

  // Navegar para match anterior
  const goToPrevMatch = () => {
    if (matches.length === 0) return;
    const newIndex = currentMatch > 0 ? currentMatch - 1 : matches.length - 1;
    setCurrentMatch(newIndex);
    highlightMatch(newIndex);
  };

  // Navegar para próximo match
  const goToNextMatch = () => {
    if (matches.length === 0) return;
    const newIndex = currentMatch < matches.length - 1 ? currentMatch + 1 : 0;
    setCurrentMatch(newIndex);
    highlightMatch(newIndex);
  };

  // Destacar match atual no textarea
  const highlightMatch = (matchIndex) => {
    if (!textareaRef.current || matchIndex < 0 || matchIndex >= matches.length) return;
    
    const match = matches[matchIndex];
    const textarea = textareaRef.current;
    
    textarea.focus();
    textarea.setSelectionRange(match.index, match.index + match.length);
    
    // Scroll para a posição
    const textBeforeMatch = value.substring(0, match.index);
    const lineHeight = 18; // Altura da linha em pixels
    const lineNumber = textBeforeMatch.split('\n').length - 1;
    const scrollTop = lineNumber * lineHeight;
    
    textarea.scrollTop = Math.max(0, scrollTop - textarea.clientHeight / 2);
  };

  // Substituir match atual
  const replaceCurrentMatch = () => {
    if (matches.length === 0 || currentMatch < 0) return;
    
    const match = matches[currentMatch];
    const before = value.substring(0, match.index);
    const after = value.substring(match.index + match.length);
    const newValue = before + replaceTerm + after;
    
    onChange(newValue);
    
    // Mover para o próximo match
    setTimeout(() => {
      if (currentMatch < matches.length - 1) {
        setCurrentMatch(currentMatch + 1);
      }
    }, 100);
  };

  // Substituir todos os matches
  const replaceAllMatches = () => {
    if (matches.length === 0) return;
    
    let newValue = value;
    
    // Substituir de trás para frente para manter os índices válidos
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const before = newValue.substring(0, match.index);
      const after = newValue.substring(match.index + match.length);
      newValue = before + replaceTerm + after;
    }
    
    onChange(newValue);
    setMatches([]);
    setCurrentMatch(-1);
  };

  // Limpar busca
  const clearSearch = () => {
    setSearchTerm('');
    setReplaceTerm('');
    setMatches([]);
    setCurrentMatch(-1);
  };

  // Manipular teclas
  const handleKeyDown = (event) => {
    const { key, ctrlKey, metaKey, shiftKey } = event;
    const isCmd = ctrlKey || metaKey;

    if (key === 'Escape') {
      onClose();
    } else if (key === 'Enter') {
      if (shiftKey) {
        goToPrevMatch();
      } else {
        goToNextMatch();
      }
      event.preventDefault();
    } else if (isCmd && key === 'h') {
      setShowReplace(!showReplace);
      event.preventDefault();
    }
  };

  if (!isOpen) return null;

  return (
    <Paper className={classes.searchContainer}>
      {/* Cabeçalho */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2">
          Buscar {showReplace && '& Substituir'}
        </Typography>
        <Box>
          <Tooltip title="Substituir (Ctrl+H)">
            <IconButton 
              size="small" 
              onClick={() => setShowReplace(!showReplace)}
              color={showReplace ? 'primary' : 'default'}
            >
              <ReplaceIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fechar (Esc)">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Campo de busca */}
      <div className={classes.searchRow}>
        <TextField
          ref={searchInputRef}
          className={classes.searchField}
          size="small"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                )}
              </InputAdornment>
            )
          }}
        />
        <div className={classes.navigationButtons}>
          <Tooltip title="Anterior (Shift+Enter)">
            <IconButton 
              size="small" 
              onClick={goToPrevMatch}
              disabled={matches.length === 0}
            >
              <UpIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Próximo (Enter)">
            <IconButton 
              size="small" 
              onClick={goToNextMatch}
              disabled={matches.length === 0}
            >
              <DownIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Informações dos matches */}
      {searchTerm && (
        <div className={classes.matchInfo}>
          <Typography variant="caption" color="textSecondary">
            {matches.length === 0 
              ? 'Nenhum resultado encontrado'
              : `${currentMatch + 1} de ${matches.length} resultados`
            }
          </Typography>
          <Box display="flex" gap={0.5}>
            <Chip
              size="small"
              label="Aa"
              color={caseSensitive ? 'primary' : 'default'}
              onClick={() => setCaseSensitive(!caseSensitive)}
              title="Diferenciar maiúsculas/minúsculas"
            />
            <Chip
              size="small"
              label=".*"
              color={useRegex ? 'primary' : 'default'}
              onClick={() => setUseRegex(!useRegex)}
              title="Usar expressão regular"
            />
            <Chip
              size="small"
              label="Ab"
              color={wholeWord ? 'primary' : 'default'}
              onClick={() => setWholeWord(!wholeWord)}
              title="Palavra inteira"
            />
          </Box>
        </div>
      )}

      {/* Seção de substituição */}
      <Collapse in={showReplace}>
        <div className={classes.replaceSection}>
          <div className={classes.searchRow}>
            <TextField
              className={classes.searchField}
              size="small"
              placeholder="Substituir por..."
              value={replaceTerm}
              onChange={(e) => setReplaceTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className={classes.navigationButtons}>
              <Tooltip title="Substituir atual">
                <IconButton 
                  size="small" 
                  onClick={replaceCurrentMatch}
                  disabled={matches.length === 0}
                >
                  <DoneIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Substituir todos">
                <IconButton 
                  size="small" 
                  onClick={replaceAllMatches}
                  disabled={matches.length === 0}
                >
                  <SelectAllIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </Collapse>
    </Paper>
  );
};

export default SearchReplace;